// R-102 (B-172, Round 3 lane 1): Choose dates takes typed dates on desktop. A native date field read typed digits one
// segment at a time (a year of "0002" while typing) and some browsers and test tools never fired a change; desktop now gets
// a plain text field that reads what was typed when the person leaves it or presses Enter. Pure, so Deno tests load it.

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const pad = (n: number) => String(n).padStart(2, '0')
function real(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 2000 || y > 2100) return null
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d ? `${y}-${pad(m)}-${pad(d)}` : null // no Feb 30
}

/**
 * "10/24/2026", "10/24/26", "10/24", "10-24-2026", "2026-10-24", "Oct 24", "October 24, 2026" -> "2026-10-24", or null.
 * A date without a year is this year's, or next year's when this year's has passed (typing "1/5" in October).
 */
export function parseTypedDate(raw: string, today: string): string | null {
  const t = raw.trim().toLowerCase().replace(/(\d)(st|nd|rd|th)\b/g, '$1')
  if (!t) return null
  const thisYear = Number(today.slice(0, 4))
  const noYear = (m: number, d: number) => { const a = real(thisYear, m, d); return a && a < today ? real(thisYear + 1, m, d) : a }
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (m) return real(Number(m[1]), Number(m[2]), Number(m[3]))
  m = t.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/)
  if (m) return m[3] ? real(m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]), Number(m[1]), Number(m[2])) : noYear(Number(m[1]), Number(m[2]))
  m = t.match(/^(?:[a-z]+,?\s+)?([a-z]{3,9})\.?\s+(\d{1,2}),?(?:\s+(\d{4}))?$/)
  if (m) {
    const mo = MONTHS.indexOf(m[1].slice(0, 3)) + 1
    if (!mo) return null
    return m[3] ? real(Number(m[3]), mo, Number(m[2])) : noYear(mo, Number(m[2]))
  }
  return null
}

/** "10/24/2026" for a stored day (what the text field shows). */
export const typedOf = (ymd: string | null | undefined): string => (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}/${ymd.slice(0, 4)}` : '')

export type RangeCheck = { ok: true; from: string; to: string | null } | { ok: false; field: 'from' | 'to'; why: 'bad' | 'past' | 'order' }
/** The typed start and optional end, checked: a real date each, not before today, the end not before the start. */
export function checkTypedRange(fromRaw: string, toRaw: string, today: string): RangeCheck {
  const from = parseTypedDate(fromRaw, today)
  if (!from) return { ok: false, field: 'from', why: 'bad' }
  if (from < today) return { ok: false, field: 'from', why: 'past' }
  if (!toRaw.trim()) return { ok: true, from, to: null }
  const to = parseTypedDate(toRaw, today)
  if (!to) return { ok: false, field: 'to', why: 'bad' }
  if (to < from) return { ok: false, field: 'to', why: 'order' }
  return { ok: true, from, to }
}
